/**
 * "Learn more" content shown on each page via LearnMoreButton — keyed by the nav route path
 * (matches Layout.tsx's MASTER_NAV `path` values). Content is written from the actual page/service
 * behavior, not generic marketing copy — keep it that way when a feature changes.
 */
export interface FeatureHelpContent {
  /** Page title shown at the top of the dialog — usually the nav label. */
  title: string;
  /** One or two sentences: what this screen is and why it exists. */
  summary: string;
  /** Concrete actions available here and what each one actually does/affects. */
  whatHappens: string[];
  /** Who typically uses this screen (roles/situations). */
  whoItsFor?: string;
  /** Extra caveats, prerequisites, or things that surprise first-time users. */
  tips?: string[];
}

export const FEATURE_HELP: Record<string, FeatureHelpContent> = {
  '/dashboard': {
    title: 'Dashboard',
    summary: 'Your landing page after login — a role-specific view: a clinical work queue for doctors, an operational/financial overview for clinic admins.',
    whatHappens: [
      "Doctor view: today's KPI strip, a Next Patient card and full queue table, an Action Required panel (draft prescriptions, pending lab orders, critical alerts) that jumps straight to the relevant screen, a Clinical Alerts + Lab Review Queue, and (if permitted) a revenue snapshot.",
      "Admin view: a 7-day/30-day toggle redraws KPI cards (Revenue, Patients, Consultations, Pending Bills) and today's appointments table.",
      '"Check-in QR" opens a printable/downloadable QR code patients can scan to self-check-in.',
      'Quick-action buttons jump to Appointments, Patients, Billing, Daily Report, Labs, Revenue & Reports, and more — filtered to what you have permission for.',
      'A Follow-ups Due panel previews patients needing a follow-up, with a link to the full queue.',
    ],
    whoItsFor: 'Every role lands here first — the content is different for doctors vs. admins, not just re-skinned.',
    tips: ['A clinic admin without a completed clinic setup gets redirected back here from most other pages until setup is finished.'],
  },
  '/notifications': {
    title: 'Mail',
    summary: 'An email-style inbox for internal clinic messages, patient-portal message threads, and a shared clinic calendar.',
    whatHappens: [
      'Sidebar folders — Inbox, Sent, Patient Messages, Calendar — switch the whole content pane; unread counts show as badges.',
      'Opening a message marks it read and lets you reply, delete, star, or step to the next/previous message.',
      'Patient Messages holds per-patient portal conversations — replying here sends directly back to that patient.',
      '"New Message" starts a new internal message to another staff member.',
      'New messages push in live — you get a toast notification if you\'re not already on that folder.',
    ],
    whoItsFor: 'Any staff member communicating internally or with patients via the portal — no role restriction.',
  },
  '/tokens': {
    title: 'Appointments',
    summary: "The doctor's/front-desk's live token queue for the day — select a patient's appointment here to open their full consultation workspace.",
    whatHappens: [
      "Selecting an appointment opens the patient workspace: vitals, AI case brief, clinical notes, recent prescriptions/visits, lab orders, and quick actions for diagnostics, billing, referrals and clinical trends.",
      "Start / Complete / No-Show / Cancel buttons move the appointment through its status lifecycle.",
      "\"Maximize\" hides the appointment list to give the workspace more room; the full-screen toggle goes edge-to-edge.",
      "Record Consultation opens an AI-assisted scribe that can draft a prescription from the conversation for you to review.",
      "New tokens/appointments and queue changes update live via WebSocket without a manual refresh.",
    ],
    whoItsFor: 'Doctors seeing patients and front-desk staff managing the day\'s queue.',
    tips: ['Vitals shown are the actual vitals recorded on the visit for this appointment, not a separate legacy record — if nothing shows, they simply haven\'t been recorded yet for this visit.'],
  },
  '/patients/register': {
    title: 'Register',
    summary: 'A one-page form to add a brand-new patient to the clinic\'s records.',
    whatHappens: [
      'Filling out and submitting the form creates a new Patient record for this clinic.',
      'If "send invite" is checked and an email is given, the patient is immediately emailed a Patient Portal registration link.',
      'On success you\'re auto-returned to the Patients directory after a couple of seconds.',
    ],
    whoItsFor: 'Front-desk staff, clinic admins, and doctors registering a patient at intake.',
    tips: ['Portal invite is optional and separate from registration — a patient can be created without portal access and invited later from the Patients directory.'],
  },
  '/patients': {
    title: 'Patients',
    summary: 'Searchable directory of every patient the clinic can see, with quick actions to open a chart, schedule a follow-up, or invite to the portal.',
    whatHappens: [
      'Search by name/mobile, filter by gender, and switch scope between This Clinic / Group (shared branches) / Transferred patients.',
      'Advanced filters (age, blood group, allergies, chronic conditions) narrow the currently loaded page — medical filters are hidden for roles without medical-records access.',
      'The calendar icon opens Schedule Follow-Up; the mail icon opens Invite/Resend Portal Access for that patient.',
      'Each row is tagged "This Clinic," "Transferred," or "Group" depending on how the patient came to be visible here.',
    ],
    whoItsFor: 'Front desk, doctors, and clinic admins looking up existing patients.',
  },
  '/inpatients': {
    title: 'Inpatients',
    summary: 'Live ward-by-ward view of currently admitted patients — used for daily rounds, admissions, and clinical monitoring.',
    whatHappens: [
      '"New Admission" admits a patient to a ward/bed.',
      'Doctors get a Round button (logs a clinical round) and an AI button that runs a comprehensive clinical analysis (assessment, red flags, suggested investigations).',
      'A pulsing "N Labs" chip appears when an admission has unreviewed completed lab results.',
      'Clinic admins get an extra "Beds & Wards" tab for configuring ward/bed capacity instead of the patient grid.',
      'Patients can be sorted sickest-first by an acuity score, with a task inbox surfacing outstanding clinical work.',
    ],
    whoItsFor: 'Doctors and staff performing rounds and clinical tasks; clinic admins additionally manage ward/bed setup.',
  },
  '/medications': {
    title: 'Medications',
    summary: "The clinic's own medication formulary — the list doctors draw from when writing prescriptions.",
    whatHappens: [
      '"Add Medication" opens a form (search an existing medicine database or type a new one) with name, generic name, strength, dosage form, manufacturer, and optional clinical info (composition, side effects, contraindications, uses).',
      'Saving adds it to the clinic list immediately, and it becomes selectable by name in the Prescriptions medicine search right away.',
      'A search box filters the table by name/generic/strength; footer stats show total and doctor-added counts.',
    ],
    whoItsFor: 'Doctors and clinic admins adding a drug that isn\'t yet in the clinic\'s formulary before it can be prescribed. Staff can view and search the list but not add to it.',
  },
  '/billing': {
    title: 'Billing',
    summary: 'Point-of-care bill builder — pick a patient, add consultation fees/lab tests/medicines from the clinic\'s fee catalog, and send the bill to the queue.',
    whatHappens: [
      'Search-select a patient; catalog tabs and search let you click to add consultation fees, lab tests, and medicines as line items.',
      'Quantities are editable inline; items can be removed or the whole bill cleared.',
      '"Finalize & Send to Queue" creates the bill and sends it to the billing counter — it does not collect payment itself.',
      'An audit log in the panel tracks the last few add/remove actions.',
    ],
    whoItsFor: 'Doctors, front-desk staff, clinic admins, and group admins creating a bill at consultation or checkout.',
    tips: ['Payment is actually collected on the Bill Queue screen, not here — this screen only builds and submits the bill.'],
  },
  '/billing/queue': {
    title: 'Bill Queue',
    summary: "The billing counter's worklist — where staff process payment, print/email receipts, and void bills.",
    whatHappens: [
      'Stat cards (Awaiting Payment, Draft, Paid, Overdue) act as quick filters, alongside search and date filters.',
      '"Process" opens an unpaid bill to collect payment; "View" opens a paid one to review it.',
      'The row menu offers Print Receipt, Email Receipt, and — with the right permission — Void Bill (requires a reason, disabled once already paid).',
      'The queue updates live as bills change status elsewhere in the app.',
    ],
    whoItsFor: 'Front-desk/billing staff and clinic admins collecting payment and managing the bill lifecycle.',
    tips: ['Voiding doesn\'t delete a bill — it marks it invalid for audit purposes; a clinic admin can reopen a voided bill within 24 hours.'],
  },
  '/follow-ups': {
    title: 'Follow-ups',
    summary: "Tracks patients due for a follow-up visit and lets staff send reminders — this is your own doctor's queue, not the clinic-wide compliance view.",
    whatHappens: [
      'Stat cards show Pending/Scheduled/Overdue counts; filters and search narrow the table; overdue rows are highlighted red, due-soon amber.',
      '"Send WhatsApp Reminder" sends a reminder — a successful response doesn\'t guarantee delivery if no messaging channel is configured.',
      '"Cancel" marks a follow-up cancelled and refreshes the queue.',
      'The list updates live as follow-ups are created, scheduled, completed, or cancelled.',
    ],
    whoItsFor: 'Clinic admins, staff, doctors, and group admins making sure patients come back for follow-ups.',
    tips: ['Reminders require WhatsApp/SMS to be configured under Admin → Engagement, or they silently fail to deliver even though the request succeeds.'],
  },
  '/doctor/schedule-config': {
    title: 'Schedule',
    summary: "Defines a doctor's recurring weekly working-hours template, which the backend uses to auto-generate bookable appointment slots.",
    whatHappens: [
      'Set daily start/end times, working days, break windows, consultation duration, and buffer between slots.',
      'The UI can auto-calculate a suggested max appointments/day from those settings.',
      '"Save Configuration" stores the template; toggling it Active tells the backend to start generating slots from it automatically. While inactive, no slots are generated.',
    ],
    whoItsFor: 'Doctors (and admins configuring on a doctor\'s behalf) setting up or changing standing weekly availability.',
    tips: ['Saving alone doesn\'t show slots here — check the Recurring/Appointments screen to see the slots this config actually generated.'],
  },
  '/doctor/appointments': {
    title: 'Recurring',
    summary: 'Despite the nav label, this is a day/date-range view of a doctor\'s generated slots and bookings — not a place to define repeating appointment series.',
    whatHappens: [
      'Pick a date range to view free/booked slots and appointments for that window.',
      'Click a free slot to Quick Book a patient into it, or Block/Unblock a slot to mark it (un)available.',
      'Reschedule or Cancel an existing booked appointment.',
    ],
    whoItsFor: 'Doctors and clinic admins managing a specific doctor\'s day-to-day slot availability and bookings.',
    tips: ["A true recurring-appointment-rule feature exists in the codebase but isn't wired into this page — despite the name, you can't create a repeating series here today."],
  },
  '/doctor/notes': {
    title: 'Notes',
    summary: 'A simple form to write and save a free-text clinical note for a patient, with a shortcut into recording vitals.',
    whatHappens: [
      'Enter a Patient ID (the patient\'s name is looked up and shown to confirm you have the right one).',
      'Type a free-text clinical note, plus any specialty-specific structured fields if the clinic has a template configured, and save.',
      '"Record Vitals" opens the vitals capture form for the same patient.',
    ],
    whoItsFor: 'Doctors documenting a visit\'s clinical note.',
    tips: ['There\'s no patient search here — you need to already know the exact Patient ID.'],
  },
  '/doctor/prescriptions': {
    title: 'Rx',
    summary: 'A basic standalone form for quickly creating a prescription (medicines with dosage/frequency/duration/instructions) for a patient.',
    whatHappens: [
      'Enter a Patient ID, add one or more medicine rows, and save — this creates the prescription directly.',
    ],
    whoItsFor: 'Doctors needing a quick way to record a prescription.',
    tips: ['This is a minimal form with no patient-name confirmation, medicine search, or template loading — the richer prescription flow (with templates, AI suggestions, medicine search) is used elsewhere, e.g. from the consultation workspace.'],
  },
  '/doctor/templates': {
    title: 'Templates',
    summary: 'Create and manage your own reusable prescription templates (chief complaint, diagnosis, notes, follow-up, medications) by condition/category.',
    whatHappens: [
      '"Create Template" defines a template\'s category, chief complaint, diagnosis, notes, follow-up days, and medication list.',
      'Search, filter by category, and star favorites; view full details or delete a template.',
      'When writing an actual prescription elsewhere, selecting a saved template auto-fills all of the above into the new prescription for you to edit before saving.',
    ],
    whoItsFor: 'Doctors who want to speed up writing prescriptions for conditions they see often.',
    tips: ['Templates are per-doctor, not clinic-shared or admin-managed.'],
  },
  '/doctor/revenue': {
    title: 'Revenue',
    summary: "A read-only dashboard of your own billing revenue — today/week/month/all-time, payment-method breakdown, and a custom date-range report.",
    whatHappens: [
      'View stat cards for revenue and bill counts across several time windows, plus a payment-method breakdown.',
      'Pick a custom date range to see a per-day revenue breakdown.',
      'Export the report as CSV, PDF, or Excel for a chosen date range.',
    ],
    whoItsFor: 'Doctors tracking their personal earnings/billing performance.',
  },
  '/doctor/feedback': {
    title: 'Feedback',
    summary: "Your own patient-satisfaction analytics — NPS score, a 6-month trend, and individual patient reviews.",
    whatHappens: [
      'View your NPS score (last 90 days) and a 6-month NPS trend chart.',
      'Browse a paginated list of patient reviews tied to your completed visits.',
    ],
    whoItsFor: 'Doctors reviewing their own patient-satisfaction data — a personal-scope version of the clinic-wide Patient Feedback admin page.',
    tips: ['This page is read-only — there\'s no way to reply to a review from here.'],
  },
  '/reports': {
    title: 'Daily Report',
    summary: "A single day's clinic performance summary — visits, new vs. follow-up patients, revenue, payment mix, and a doctor-by-doctor breakdown.",
    whatHappens: [
      'Pick a date and the report reloads: total visits, new patients, follow-ups, total/average revenue.',
      'View a Cash/Card/UPI breakdown and a doctor-wise performance table.',
      '"Print" opens the browser print dialog for the report; "Export Vitals PDF" opens a separate vitals-export page.',
    ],
    whoItsFor: 'Doctors, clinic admins, and group admins reviewing a single day\'s operational/financial performance.',
    tips: ['The "Peak Hour" figure shown is a fixed placeholder, not computed from real data — don\'t rely on it.'],
  },
  '/lab/tasks': {
    title: 'Orders',
    summary: "The lab assistant's work queue for processing lab orders doctors have placed — entering results, reviewing history, and watching for critical values.",
    whatHappens: [
      'Pending Requisitions tab lists open orders; opening one shows every ordered test for that visit with fields to enter results or upload a report (including an AI auto-fill from an uploaded report).',
      '"Complete All Tests" marks each test with input as completed — only tests you\'ve actually entered a value or attached a report for are submitted; the rest stay pending.',
      'Once every test on a requisition is done, it moves to Processed History.',
      'Critical Results tab surfaces HIGH/CRITICAL values from the last 7 days with a jump-to-patient action.',
      'New orders and critical values push in live over WebSocket.',
    ],
    whoItsFor: 'Lab assistants (and clinic admins) fulfilling lab orders doctors placed on patients.',
    tips: ['This is the ONLY place that actually marks a lab order as completed — uploading a report elsewhere (e.g. a doctor\'s "Upload Lab" button) does not change an order\'s status.'],
  },
  '/pharmacy/inventory': {
    title: 'Medication Stock',
    summary: "The pharmacy's own medication catalog and stock-management screen — separate from the clinic's prescribing formulary.",
    whatHappens: [
      '"Add Medication" (managers only) creates a new stock entry; Edit updates an existing one.',
      'Filter by All / Low Stock / Expired / Expiring Soon, with live counts.',
      'Per medication: Update Stock, Update Price, Adjust Inventory (manual correction with an audit reason), View History, or Delete.',
    ],
    whoItsFor: 'Pharmacy admins/staff primarily, but also reachable by clinic admins and doctors running an in-house pharmacy.',
    tips: ['Plain pharmacy staff (without manager rights) get a view-oriented table — add/edit/stock/price/delete controls only show for manager-level roles.'],
  },
  '/admin/hospital-groups': {
    title: 'Hospital Groups',
    summary: 'Manages hospital chains ("groups") that own multiple clinic branches, including their subscription plan and status.',
    whatHappens: [
      '"New Group" creates a hospital group with a name, unique code, and plan type (which caps how many branches it can have).',
      'Suspend/Activate toggles a group\'s status; Delete soft-deletes it (blocked if it still has active branches) and moves it to a restorable Deleted tab.',
      'A group_admin opening this page is auto-redirected to their own group\'s page.',
    ],
    whoItsFor: 'Super admins and group admins overseeing multi-branch hospital chains.',
    tips: ['Despite living under "Management," this page is NOT accessible to a regular clinic_admin — only super_admin, group_admin, and regional_admin.'],
  },
  '/admin/inventory': {
    title: 'Supplies & Consumables',
    summary: "Manages the clinic's general-store inventory (consumables, surgicals, PPE, IV fluids, lab reagents, devices) — separate from prescription medicines.",
    whatHappens: [
      '"Add Inventory Item" creates a stock item with SKU, category/unit/location, quantity, reorder level, and optional batch/expiry.',
      'A configuration panel edits the category/unit/location dropdown lists and default low-stock threshold.',
      'Each saved item shows a live status chip (In Stock / Low Stock / Out of Stock) computed from quantity vs. reorder level.',
    ],
    whoItsFor: 'Clinic admins (and group admins) responsible for procurement and stock control of non-pharmacy supplies.',
    tips: ['Prescription medicines dispensed to patients live in a separate screen — Pharmacy → Medication Stock — not here.'],
  },
  '/admin/staff': {
    title: 'Staff',
    summary: 'Invite, activate/deactivate, and change roles for clinic staff and doctors.',
    whatHappens: [
      '"Invite Staff" sends an email invitation — the person is NOT created as an active login immediately; they set their own password via a link that expires in 7 days.',
      'Pending Invitations can be resent without invalidating the original link.',
      'Deactivate immediately revokes a staff member\'s login/resource access (you can\'t deactivate your own account).',
      'Roles (Staff, Doctor, Clinic Admin, Lab Assistant, Pharmacist) can be changed inline with an immediate-effect confirmation.',
    ],
    whoItsFor: 'Clinic admins managing personnel and access levels.',
    tips: ['Inviting someone is not the same as giving them access instantly — nothing happens until they accept the email and set a password.'],
  },
  '/admin/fee-schedule': {
    title: 'Fee Schedule',
    summary: "The clinic's master price list for consultations, lab tests, pharmacy items, and procedures.",
    whatHappens: [
      'Five tabs (Consultation Fees, Lab Tests, Pharmacy Items, Procedures, Packages) let you add, edit, duplicate, and delete priced entries.',
      'A bulk upload path exists for adding many fees at once.',
      'Changes here automatically flow into billing at consultation completion, lab ordering, and prescribing — this is the live source of truth for prices, not just a reference list.',
    ],
    whoItsFor: 'Clinic admins who set and maintain official pricing.',
    tips: ['Editing or deleting a fee changes what patients get billed going forward — it\'s not a passive catalog.'],
  },
  '/settings/doctors': {
    title: 'Doctors',
    summary: "Edit doctor profiles for the clinic and manage which clinics each doctor is assigned to.",
    whatHappens: [
      'Edit a doctor\'s name, specialization, registration number, department, OPD fee, and consultation duration.',
      'Deactivate a doctor (doctors can\'t be deleted, only deactivated).',
      'Clinic Assignments lets you attach a doctor to additional clinics or remove them from a non-primary one — their primary/home clinic can\'t be removed.',
    ],
    whoItsFor: 'Clinic admins (and group admins across branches) managing doctor rosters, fees, and cross-clinic assignments.',
  },
  '/settings/departments': {
    title: 'Departments',
    summary: "Defines the clinic's departments/wards and, via an optional Specialty Template, unlocks specialty-specific vitals/note fields for doctors in that department.",
    whatHappens: [
      '"Add Department" sets a name, code, optional description, display order, and an optional Specialty Template.',
      'Selecting a Specialty Template (e.g. antenatal, well-child) is what actually turns on extra clinical fields for doctors assigned to that department — leaving it "General" changes nothing.',
      'Deactivate turns a department off; there\'s no delete.',
    ],
    whoItsFor: 'Clinic admins (and group admins) organizing clinic structure and enabling specialty-aware documentation.',
  },
  '/admin/nav-settings': {
    title: 'Nav Settings',
    summary: 'Controls which left-navigation menu items are visible to each staff role in this clinic — a display setting, not a security control.',
    whatHappens: [
      'Pick a role tab (Doctor, Staff, Clinic Admin, etc.) and toggle "Visible" per route to show/hide it in that role\'s sidebar.',
      'Reset removes a clinic-specific override and reverts that route to the platform default for that role.',
    ],
    whoItsFor: 'Clinic admins tailoring the menu each role sees, e.g. simplifying navigation for staff.',
    tips: ['This does NOT restrict access — hiding a nav item doesn\'t block the underlying page if someone reaches it by URL. Use Role Permissions for actual access control.'],
  },
  '/admin/erasure-requests': {
    title: 'Data Erasure',
    summary: "Review queue for patient right-to-erasure (DPDP/GDPR-style) requests, submitted via the patient portal or entered by staff.",
    whatHappens: [
      'Approve requires typing how you verified the patient\'s identity, then immediately anonymizes their name, mobile, email, address, and date of birth — this cannot be undone.',
      'Clinical records and billing history are explicitly NOT deleted on approval — only identifying fields are scrubbed.',
      'Reject records an optional reason and leaves the patient\'s data untouched.',
    ],
    whoItsFor: 'Clinic admins (and super admins) handling data-protection/right-to-erasure compliance.',
    tips: ['Approval is irreversible and only anonymizes PII — don\'t expect full record deletion.'],
  },
  '/admin/ai-settings': {
    title: 'AI Settings',
    summary: 'Enable/disable specific AI-assisted features clinic-wide and set monthly usage quotas for each.',
    whatHappens: [
      'Each AI feature (lab summary, visit summary, billing assistant, staff chat, prescription safety check, and more) has its own Enabled switch.',
      'Turning a feature off requires typing a reason and immediately hides that feature for everyone in the clinic.',
      'Quota lets you set a monthly request cap per feature (0 falls back to the system default); usage is shown live and flagged near the limit.',
    ],
    whoItsFor: 'Clinic admins controlling which AI capabilities are active and managing usage costs.',
  },
  '/admin/engagement': {
    title: 'Engagement',
    summary: 'Configures automated patient engagement — appointment reminders and follow-up recall messages — plus optional WhatsApp Business integration.',
    whatHappens: [
      'Toggle appointment reminders (with hours-before setting) and follow-up recall messages (with days-before setting).',
      'Configure WhatsApp Business (Meta Cloud API or Gupshup) with an access token and sender number to enable WhatsApp delivery alongside email.',
      'A live outbox table shows recent send attempts with status and error messages for failures.',
    ],
    whoItsFor: 'Clinic admins managing patient communication to reduce no-shows and improve follow-up compliance.',
    tips: ['Messages go out via email/in-app by default even without WhatsApp configured — WhatsApp is an additive channel, not a requirement.'],
  },
  '/admin/checkin-settings': {
    title: 'Check-In',
    summary: 'Configures the patient digital pre-visit check-in flow — the form patients fill out via a sent link or walk-in QR code.',
    whatHappens: [
      'A master toggle turns digital check-in on/off; link expiry and auto-token-on-completion are configurable.',
      'Choose which notification channels (SMS/Email/WhatsApp) send the check-in link, and set OTP delivery preference.',
      'Set each form section (Personal Info, Emergency Contact, Medical History, Consent, etc.) to Hidden/Optional/Required, and add custom questions with age-gating or conditional follow-up logic.',
      'Walk-in QR generates a printable QR code for front-desk self-check-in, once digital check-in is enabled.',
    ],
    whoItsFor: 'Clinic admins configuring the pre-visit intake experience.',
    tips: ['The Walk-in QR option stays disabled until "Enable Digital Check-In" is turned on.'],
  },
  '/admin/health-packages': {
    title: 'Packages',
    summary: 'Create and sell fixed-price health/membership packages that bundle consultation credits and discounts, then enroll patients.',
    whatHappens: [
      '"New Package" defines a name, price, validity period, included consultation credits, and lab/medicine discount percentages.',
      '"Enroll Patient" searches for a patient and signs them up for that package at its listed price and validity.',
      'Deactivate stops new enrollments into a package.',
    ],
    whoItsFor: 'Clinic admins setting up preventive-care/membership offerings that staff enroll patients into.',
    tips: ['Deactivating a package doesn\'t cancel patients already enrolled in it.'],
  },
  '/admin/corporate': {
    title: 'Corporate',
    summary: 'Manages corporate/employer health contracts (companies that pay for staff healthcare) and tracks enrolled employees under each.',
    whatHappens: [
      '"New Contract" creates a corporate contract — company details, contract value, employee count, consultation/lab discount %, billing cycle, and dates.',
      'Selecting a contract switches to its Employees tab, showing everyone currently enrolled under it.',
      'Edit updates contract terms; Cancel (active contracts only) ends the contract.',
    ],
    whoItsFor: 'Clinic admins setting up and managing corporate/employer wellness contracts.',
    tips: ['Creating a contract doesn\'t enroll employees by itself — there\'s no "add employee" action on this page; enrollment happens elsewhere.'],
  },
  '/admin/revenue-intelligence': {
    title: 'Revenue & Reports',
    summary: 'A read-only analytics dashboard combining revenue trends, doctor performance, patient demographics, and rule-based suggestions to grow/recover revenue.',
    whatHappens: [
      'KPI tiles for 30-day revenue/patients/consultations/pending bills, with growth vs. the prior period.',
      'Rule-based "Revenue Improvement Suggestions" with severity and estimated monthly impact — informational, not automatic actions.',
      'Charts for revenue trend + forecast, per-doctor revenue, payment mix, demographics, and top services.',
      'A Lost Revenue panel estimates losses from no-shows, overdue follow-ups, and unpaid bills; an Outstanding Receivables panel ages unpaid bills.',
    ],
    whoItsFor: 'Clinic admins reviewing financial and operational performance.',
    tips: ['Suggestions are rule-based, not AI-generated, despite reading like recommendations.'],
  },
  '/admin/feedback-nps': {
    title: 'Patient Feedback',
    summary: 'Shows NPS score and patient reviews collected after completed visits, clinic-wide, with an optional per-doctor filter.',
    whatHappens: [
      'Pick "All doctors" or a specific doctor to filter the NPS score, trend, and review list shown.',
    ],
    whoItsFor: 'Clinic admins monitoring patient satisfaction clinic-wide or per doctor.',
  },
  '/admin/quality-measures': {
    title: 'Quality Measures',
    summary: 'A chronic-disease registry showing testing and control rates for tracked conditions (e.g. diabetes, hypertension) across the clinic.',
    whatHappens: [
      'Each condition shows a "Confirmed" cohort (formally coded diagnosis) and a "Suspected" cohort (mentioned in notes but not yet coded).',
      'Testing Rate and Control Rate are computed live from lab/vitals data — labeled as general clinical targets, not individualized advice.',
      '"Show patient list" expands a per-condition table sorted to surface untested patients first, then uncontrolled, then controlled.',
    ],
    whoItsFor: 'Clinic admins and doctors wanting clinic-wide chronic-disease testing/control compliance.',
    tips: ['This page is read-only — properly coding a "Suspected" patient\'s diagnosis (done elsewhere, in the prescription/diagnosis workflow) is what moves them into "Confirmed" and improves the numbers.'],
  },
  '/admin/follow-ups': {
    title: 'Follow-ups',
    summary: 'A clinic-wide, admin-facing compliance view of overdue/due/upcoming follow-ups across ALL doctors — distinct from the doctor-facing personal follow-up queue.',
    whatHappens: [
      'Overdue / Due Today / Upcoming tabs list every doctor\'s follow-ups, not just one.',
      '"Remind" sends a one-click WhatsApp/SMS reminder (disabled if the patient has no mobile number on file).',
    ],
    whoItsFor: 'Clinic admins and staff monitoring follow-up compliance across the whole clinic.',
    tips: ['Doctors don\'t see this admin enforcement view — they only see their own queue at /follow-ups.'],
  },
  '/admin/referrals': {
    title: 'Referrals',
    summary: 'Tracks how new patients heard about the clinic and the status/rewards of patient-to-patient referrals.',
    whatHappens: [
      'View summary stats (Total Referrals, Converted, Conversion Rate) and a source breakdown.',
      'Browse recent referrals with status (Pending/Converted/Rewarded/Cancelled) and reward info.',
    ],
    whoItsFor: 'Clinic admins and staff reviewing referral-driven patient acquisition and reward payouts.',
    tips: ['This page is read-only — referral data originates from the patient registration flow, not from here.'],
  },
  '/growth': {
    title: 'Follow-up List',
    summary: 'A plain-language patient recall/re-engagement dashboard showing which patients are likely to stop returning or need medical attention.',
    whatHappens: [
      '"To follow up" and "May not return" tabs group patients by a computed retention-risk band, alongside a separate "medical alert" status.',
      '"Send reminder" emails that one patient a recall message (capped at once per day).',
      '"Quick summary" (doctors/super admin) shows an AI-generated patient brief with a print option.',
      '"Update list" (admins) forces an immediate recompute of retention/risk scores.',
    ],
    whoItsFor: 'Doctors and clinic admins wanting a prioritized list of patients to proactively call back.',
    tips: ['The list recomputes automatically once a day — "Update list" just forces it early. For sending to many patients at once, use Bulk Reminders instead.'],
  },
  '/growth/campaigns': {
    title: 'Bulk Reminders',
    summary: 'Send the same reminder message to a filtered group of patients at once — currently by email only.',
    whatHappens: [
      '"New bulk reminder" defines a target segment (e.g. patients inactive 6+ months, optionally filtered by chronic condition), an email subject/body with placeholders, a send mode (manual approve vs. fully automatic), and a schedule.',
      '"Build list" runs the segment query and, in automatic mode, sends immediately.',
      'For manual-approve campaigns, each recipient can be approved and sent individually.',
    ],
    whoItsFor: 'Clinic admins running proactive patient re-engagement email blasts to segments of patients.',
    tips: ['Despite the name "Bulk Reminders," sends today go out by email only — SMS/WhatsApp isn\'t wired up yet. Each patient is never sent the same campaign twice.'],
  },
  '/profile': {
    title: 'My Profile',
    summary: 'View and edit your own personal profile, upload a photo, and manage account security settings.',
    whatHappens: [
      'Upload a profile photo (max 5MB) or fall back to an auto-generated avatar.',
      'Update your name, mobile, email, address, specialization, and bio.',
      'Clinic admins and doctors additionally get an App Lock/PIN panel; clinic admins, super admins, and group admins get a Two-Factor Authentication panel.',
    ],
    whoItsFor: 'Any authenticated user managing their own account.',
  },
};
